import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Navigate } from 'react-router';
import styled from 'styled-components';
import DeleteFileModal from '../components/DeleteFileModal';
import { emitNotification } from '../components/Notification';
import TherapyDetails from '../components/TherapyDetails';
import SessionList from '../components/SessionList';
import WorkoutsContainer from './Workouts/WorkoutsContainer';
import { useAuthFetchFromRestAPI } from '../util/api';
import { sendRoomInviteEmail } from '../util/roomInviteEmail';
import { Accordion, Alert, Row, Col, AccordionHeader } from 'react-bootstrap';
import QuestionnaireContainer from './Proms/PromsContainer';
import { IAppointment, ITherapy } from '../util/types';
import SecondaryButton from '../components/SecondaryButton';
import AppointmentList from '../components/appointment/AppointmentList';
import i18next from 'i18next';
import AnswerAndActivity from '../components/activity/AnswerAndActivity';
import { useAuth0 } from '@auth0/auth0-react';
import { Trans } from 'react-i18next';

type DurationMin = 15 | 30 | 45 | 60;
type CreatePayload = { start: string; end: string; duration: DurationMin };

const Loader = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-grow: 1;
  font-size: 30px;
  justify-content: center;
  padding-top: 100px;
  color: white;
`;

const AppointmentDiv = styled.div`
    max-height: 40vh;
    overflow-y: auto;
    overflow-x:hidden;
`;

const alertBox = {
  background: '#ffcc4a',
  borderColor: '#ffcc4a',
  color: '#000000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
} as const;

const PatientDetailsContainer: FC = () => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const { user } = useAuth0();
  const { therapyId } = useParams<{ therapyId: string }>();

  const [patientEmail, setPatientEmail] = useState<{ email: string }>();
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [therapy, setTherapy] = useState<ITherapy>();
  const [createTrainingPlanForSessionID, setCreateTrainingPlanForSessionId] = useState<string | null>(null);
  const [fileToEdit, setFileToEdit] = useState<any>(null);
  const [scheduledSessions, setScheduledSessions] = useState<any[]>([]);
  const [scheduleNewSession, setScheduleNewSession] = useState<boolean | null>(null);
  const [scheduledAppointments, setScheduledAppointments] = useState<IAppointment[]>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [resending, setResending] = useState(false);

  async function editFile(file: any) {
    const response = await authFetch(`/api/v1/files`, {
      method: 'POST',
      body: file,
    });

    const update: ITherapy = {
      ...therapy,
      sessions: therapy!.sessions.reduce((memo, session) => {
        if (session.id !== response.session) {
          memo.push(session);
        } else {
          memo.push({
            ...session,
            images: session.images.reduce((imgs, image) => {
              if (image.id !== response.id) {
                imgs.push(image);
              } else if (!response.deleted) {
                imgs.push(response);
              }
              return memo;
            }, []),
          });
        }

        return memo;
      }, []),
    };

    setTherapy(update);
    setPatientEmail({ email: update.patient.email });
  }

  async function removeFile() {
    await editFile({ ...fileToEdit, deleted: true });
    setFileToEdit(null);
  }

  const fetchAllUpcomingScheduledSessions = async () => {
    const scheduledSessionsFromRemote = await authFetch('/api/v1/scheduled-sessions');
    setScheduledSessions(Array.isArray(scheduledSessionsFromRemote ? scheduledSessionsFromRemote : []));
  };

  const fetchAllUpcomingScheduledAppointment = async () => {
    if (!therapy) {
      return;
    }

    const appointmentFromRemote = await authFetch(`/api/v1/appointment/patient/web/${therapy.patient._id}`);
    setScheduledAppointments(Array.isArray(appointmentFromRemote) ? appointmentFromRemote : []);
  };

  const sendNewScheduledSessionInviteEmail = async (scheduledSession, emailAddress, inviteMessage) => {
    await sendRoomInviteEmail(authFetch, {
      to: emailAddress,
      patientLastName: therapy!.lastName,
      patientFirstName: therapy!.firstName,
      inviteMessage: inviteMessage,
      therapistFullname: user?.name,
      patientId: therapy!.id,
      sessionDate: scheduledSession.start,
    });
    emitNotification(`Einladung erfolgreich an '${emailAddress}' versendet!`);
  };

  const resentActivationEmail = async () => {
    try {
      setResending(true);
      await fetch('api/v1/patients/resend-email/', {
        method: 'POST',
        body: { accountId: therapy!.patient!.accountId },
      });
      emitNotification(i18next.t('therapyDetails.resendActivationEmail.successMsg', patientEmail));
    } catch {
      emitNotification(i18next.t('therapyDetails.resendActivationEmail.errorMsg'));
    } finally {
      setResending(false);
    }
  };

  const createNewScheduledSession = async (
    scheduledSessionDate,
    shouldSendEmailInvite,
    emailAddress,
    emailInviteMessage
  ) => {
    setScheduleNewSession(false);

    const scheduledSessionFromRemote = await authFetch('/api/v1/scheduled-sessions', {
      method: 'POST',
      body: scheduledSessionDate,
    });
    setScheduledSessions((prev) => [scheduledSessionFromRemote, ...prev]);

    if (shouldSendEmailInvite) {
      await sendNewScheduledSessionInviteEmail(scheduledSessionFromRemote, emailAddress, emailInviteMessage);
    }
  };

  const fetchTherapyDetails = async (): Promise<void> => {
    const therapyData = await authFetch(`/api/v1/therapies/${therapyId}`);
    setTherapy(therapyData);
    setPatientEmail({ email: therapyData.patient.email });
  };

  useEffect(() => {
    if (therapy) {
      void fetchAllUpcomingScheduledAppointment();
    }
  }, [therapy]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const run = async () => {
      try {
        await fetchTherapyDetails();
        setIsFetchingData(false);
        await fetchAllUpcomingScheduledSessions();
      } catch (error) {
        console.error('Error occured while fetching patient details data', error);
      }
    };

    void run();
  }, [user]);

  if (isFetchingData) {
    return (
      <Loader>
        <i className="fas fa-spin fa-cog" />
      </Loader>
    );
  }

  if (createTrainingPlanForSessionID) {
    return <Navigate to={`/patienten/${therapyId}/trainingsplan/${createTrainingPlanForSessionID}`} />;
  }

  const closeButtonNavigateTo = '/therapien';

  const patientDetailsChildren = (
    <>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
              <span className="fs-4">{i18next.t('therapyDetails.appointments.title')}</span>
              <SecondaryButton
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  setShowAppointmentModal(true);
                }}
              >
                {i18next.t('therapyDetails.appointments.button')}
              </SecondaryButton>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {scheduledAppointments && therapy ? (
              <Row>
                <Col md={12}>
                  <AppointmentDiv>
                    <AppointmentList
                      therapy={therapy}
                      appointmentList={scheduledAppointments}
                      isShow={showAppointmentModal}
                      onModalHide={(isHide) => setShowAppointmentModal(isHide)}
                      onCreateAppointment={async (payload: CreatePayload) => {
                        const response = await authFetch('/api/v1/appointment/', {
                          method: 'POST',
                          body: {
                            patient: therapy?.patient._id,
                            start: payload.start,
                            end: payload.end,
                            duration: payload.duration,
                          },
                        });

                        if (response && !response.error) {
                          setScheduledAppointments((prev) => [response, ...prev]);
                        }
                      }}
                    />
                  </AppointmentDiv>
                </Col>
              </Row>
            ) : null}

            {scheduledSessions && scheduledSessions.length > 0 ? (
              <SessionList
                patient={therapy}
                scheduledSessions={scheduledSessions}
                onCreateTrainingPlan={(sessionId) => {
                  setCreateTrainingPlanForSessionId(sessionId);
                }}
                onRemoveFile={setFileToEdit}
                onScheduleNewSession={() => setScheduleNewSession(true)}
                onCreateManualSession={async ({ start, end }) => {
                  const newSession = await authFetch('api/v1/session/', {
                    method: 'POST',
                    body: { patient: therapy!.id, start, end },
                  });
                  setTherapy((pd: any) => ({ ...pd, sessions: [newSession, ...pd.sessions] }));
                }}
              />
            ) : null}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <AccordionHeader>
            <span className="fs-4">{i18next.t('therapyDetails.workouts.title')}</span>
          </AccordionHeader>
          <Accordion.Body>{therapy ? <WorkoutsContainer therapy={therapy} /> : null}</Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <span className="fs-4">{i18next.t('therapyDetails.questionnaire.title')}</span>
          </Accordion.Header>
          <Accordion.Body>{therapy ? <QuestionnaireContainer therapy={therapy} /> : null }</Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <span className="fs-4">{i18next.t('therapyDetails.answerAndActivity.title')}</span>
          </Accordion.Header>
          <Accordion.Body>{therapy ? <AnswerAndActivity therapy={therapy} /> : null }</Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <DeleteFileModal show={fileToEdit} onDelete={removeFile} onAbort={() => setFileToEdit(null)} />
    </>
  );

  return (
    <>
      {!therapy!.patient?.isEmailVerified ? (
        <Alert variant="warning" style={alertBox}>
          <Row>
            <Col md={12} xs={10}>
              <Trans i18nKey="therapyDetails.resendActivationEmail.body" values={patientEmail} />
              &nbsp;&nbsp;&nbsp;
              <SecondaryButton onClick={resentActivationEmail} disabled={resending}>
                {resending ? i18next.t('generic.loading') : i18next.t('therapyDetails.resendActivationEmail.button')}
              </SecondaryButton>
            </Col>
          </Row>
        </Alert>
      ) : null}

      <TherapyDetails user={user} therapy={therapy!} closeButtonNavigateTo={closeButtonNavigateTo}>
        {patientDetailsChildren}
      </TherapyDetails>
    </>
  );
};

export default PatientDetailsContainer;
